// Libraries
import { NumberInput, NumberInputField, SystemStyleObject } from '@chakra-ui/react';
// Types
import { CustomNumberInputProps } from './CustomNumberInput.types';

/**
 * A customizable number input component with a currency prefix.
 *
 * @component
 * @param {CustomNumberInputProps} props - The props for the CustomNumberInput component.
 * @param {boolean} props.isDisabled - Determines if the input is disabled.
 * @param {number} props.maxLength -  The max length to use in the input field.
 * @param {Dispatch<SetStateAction<string | undefined>>} props.setValue - Function to set the input's value.
 * @param {number} props.rest - All rest props from chakra NumberInput
 * @returns {ReactElement} The CustomNumberInput component.
 */
export const CustomNumberInput = ({
  isDisabled,
  setValue,
  maxLength = 5,
  isQuantity = false,
  ...rest
}: CustomNumberInputProps) => {
  const color = isDisabled ? 'border.100' : 'text.faded';
  const paddingLeft = isQuantity ? 2 : 7;

  const pseudoClass: SystemStyleObject = isQuantity
    ? {
        _after: {
          position: 'absolute',
          content: `'MT'`,
          lineHeight: 7,
          fontSize: 'sm',
          color,
          zIndex: 1,
          right: 2,
        },
      }
    : {
        _before: {
          position: 'absolute',
          content: `'$'`,
          lineHeight: 7,
          fontSize: 'sm',
          left: 2,
          color,
          zIndex: 1,
        },
      };

  return (
    <NumberInput
      allowMouseWheel
      isDisabled={isDisabled}
      errorBorderColor='text.error'
      step={10}
      maxW={28}
      keepWithinRange={false}
      clampValueOnBlur={false}
      onChange={setValue}
      position='relative'
      border='section'
      borderRadius='md'
      size='sm'
      sx={{
        ...pseudoClass,
      }}
      {...rest}
    >
      <NumberInputField
        h={7.5}
        pl={paddingLeft}
        border='none'
        maxLength={maxLength}
        paddingInlineEnd={0}
        _focusVisible={{
          _invalid: {
            boxShadow: 'errorOutline',
          },
          boxShadow: 'outline',
        }}
      />
    </NumberInput>
  );
};
